import '../App.css';
import { useApolloClient, gql } from '@apollo/client';
import { useEffect, useState, useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer, Tooltip } from 'react-leaflet';
import { useNavigate, useParams } from 'react-router-dom';

const GET_DATA_PAGINATED = gql`
  query GetMicronarrativas($after: String, $first: Int = 100, $order: OrderEnum = ASC) {
    micronarrativas(first: $first, after: $after, where: { orderby: { field: DATE, order: $order } }) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          micronarrativasMeta {
            hotspot
            data
            latitude
            longitude
            tags_micro {
              edges {
                node {
                  id
                  name
                }
              }
            }
            narrativa
            imagem {
              node {
                mediaItemUrl
              }
            }
            autor
          }
        }
      }
    }
  }
`;

export const areas = [
  { hotspot: "A", area: "Orla", geo: [-22.98636907181226, -43.215548215012205] },
  { hotspot: "C", area: "Orla", geo: [-22.98636907181226, -43.215548215012205] },
  { hotspot: "D", area: "Manejo do Canal", geo: [-22.986039934573228, -43.215571120394415] },
  { hotspot: "E", area: "Rua Prudente de Morais", geo: [-22.98473967712194, -43.21552278138476] },
  { hotspot: "F", area: "Rua Prudente de Morais", geo: [-22.98473967712194, -43.21552278138476] },
  { hotspot: "G", area: "Parcão", geo: [-22.984241284021596, -43.21527032455299] },
  { hotspot: "H", area: "Rua Visconde de Pirajá", geo: [-22.983704833823385, -43.215100851403434] },
  { hotspot: "I", area: "Rua Visconde de Pirajá", geo: [-22.983704833823385, -43.215100851403434] },
  { hotspot: "J", area: "Praça Grécia", geo: [-22.982750483165365, -43.21472217086022] },
  { hotspot: "K", area: "Praça Grécia", geo: [-22.982750483165365, -43.21472217086022] },
  { hotspot: "L", area: "Praça Grécia", geo: [-22.982750483165365, -43.21472217086022] },
  { hotspot: "M", area: "Rua Redentor", geo: [-22.98194452899564, -43.21456031499833] },
  { hotspot: "N", area: "Rua Redentor", geo: [-22.98194452899564, -43.21456031499833] },
  { hotspot: "O", area: "Área Comlurb", geo: [-22.981387034203873, -43.21427792237392] },
  { hotspot: "P", area: "Área Comlurb", geo: [-22.981387034203873, -43.21427792237392] },
  { hotspot: "Q", area: "Área Comlurb", geo: [-22.981387034203873, -43.21427792237392] },
  { hotspot: "R", area: "Lagoa", geo: [-22.980340440343294, -43.2140352311156] },
  { hotspot: "S", area: "Lagoa", geo: [-22.980340440343294, -43.2140352311156] },
  { hotspot: "T", area: "Lagoa", geo: [-22.980340440343294, -43.2140352311156] },
];

export function getArea(hotspotId) {
  const letter = hotspotId[0];
  const selection = areas.find(item => item.hotspot === letter);
  return selection ? selection : null;
}

function useHotspotData(hotspotId) {
  const client = useApolloClient();
  const [state, setState] = useState({ loading: true, error: null, data: [] });

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      let after = null;
      const allEdges = [];

      try {
        do {
          const { data } = await client.query({
            query: GET_DATA_PAGINATED,
            variables: { after, first: 100, order: "ASC" },
            fetchPolicy: 'network-only',
          });

          const conn = data?.micronarrativas;
          if (!conn) break;

          allEdges.push(...(conn.edges || []));
          after = conn.pageInfo?.endCursor || null;

          if (!conn.pageInfo?.hasNextPage) break;
        } while (true);

        if (cancelled) return;

        console.log(`Finished fetching all entries: ${allEdges.length} total`);

        const filtered = allEdges
          .filter(({ node }) => node?.micronarrativasMeta?.hotspot === hotspotId)
          .map(({ node }) => ({
            id: node.id,
            narrativa: node.micronarrativasMeta?.narrativa ?? "",
            data: node.micronarrativasMeta?.data ?? "",
            tags: node.micronarrativasMeta?.tags_micro?.edges ?? [],
            imagem: node.micronarrativasMeta?.imagem?.node?.mediaItemUrl ?? "",
            autor: node.micronarrativasMeta?.autor ?? "",
          }));

        setState({ loading: false, error: null, data: filtered });
      } catch (error) {
        if (cancelled) return;
        setState({ loading: false, error, data: [] });
      }
    }

    setState(prev => ({ ...prev, loading: true }));
    fetchAll();

    return () => { cancelled = true; };
  }, [client, hotspotId]);

  return state;
}

export default function Hotspots() {
  const [index, setIndex] = useState(0);
  const { hotspotId } = useParams();
  const { loading, error, data: micronarrativas } = useHotspotData(hotspotId);
  const navigate = useNavigate();
  const selection = useMemo(() => getArea(hotspotId), [hotspotId]);

  const handleTagClick = (tag) => {
    window.open(`/tags/${tag}`, '_blank');
  };

  const handleAreaClick = (area) => {
    window.open(`/areas/${area}`, '_blank');
  };

  const handleClick = (newIndex) => {
    setIndex(newIndex);
  };

  if (loading) return <p>Loading...</p>;
  if (error)   return <p>Error: {error.message}</p>;
  if (!micronarrativas.length) return <p>No data found for the selected hotspot.</p>;

  return (
    <div className='Wrapper'>
      <div className='Thumbnails'>
        {micronarrativas.map((item, id) => (
          <div
            onClick={() => handleClick(id)}
            className='Thumb'
            key={item.id}
            style={id === index ? { opacity: '100%' } : {}}
          >
            <img src={item.imagem} alt={`Thumbnail ${id}`} />
          </div>
        ))}
      </div>

      <div className='Imagem'>
        <img src={micronarrativas[index].imagem} alt="Selected" />
      </div>

      <div className='Narrativa'>
        <div className='Corpo'>
          <div className='Texto'>
            <span>{micronarrativas[index].narrativa}</span>
            <div className='Sub-texto'>
              Por <span>{micronarrativas[index].autor}</span> -{' '}
              <span>
                {new Date(micronarrativas[index].data).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
              </span>
            </div>
          </div>

          <div className='Tags'>
            {micronarrativas[index].tags.map(({ node }) => (
              <div
                key={node.id}
                className='Tag'
                onClick={() => handleTagClick(node.name)}
                style={{ cursor: 'pointer' }}
              >
                <span>{node.name} </span>
              </div>
            ))}
          </div>
        </div>

        {/* Guard in case selection is null */}
        {selection && (
          <div style={{ height: '150px', width: '100%' }}>
            <MapContainer
              center={selection.geo}
              zoom={15}
              scrollWheelZoom={false}
              dragging={false}
              style={{ height: '150px', width: '100%' }}
              zoomControl={false}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker
                position={selection.geo}
                eventHandlers={{
                  click: () => {
                    handleAreaClick(selection.area);
                  },
                }}
              >
                <Tooltip direction="top" offset={[-15, -15]} opacity={1} permanent={false}>
                  {selection.area}
                </Tooltip>
              </Marker>
            </MapContainer>
          </div>
        )}
      </div>
    </div>
  );
}
